import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfRect,
  PdfPoint,
  PdfBrush,
  PdfColor,
  ETextAlign,
  PageGen,
  PdfFont,
} from '../../pdf/pdf';
import { ILayoutImageGen } from '../layoutImage';
import { OptsImage } from '../optsImage';
import { IItemCount } from '@educraft/base';
import { GUtil } from '../../pdf/gutil';
import { drawDitherLogo } from '../../pdf/ditherLogo';
import { ESymbol } from '../../pdf/draw/symbol';
import '../../pdf/draw/primitives';
import '../../pdf/draw/image';
import '../../pdf/draw/cut';
import '../../pdf/draw/string';
import '../../pdf/draw/symbol';

/**
 * Hraci_karty - Playing cards
 *
 * 16 landscape pages:
 * - 2 sets (Blue backs, Red backs), each with:
 *   - 1 page of DitherLogo "KARTY" backs
 *   - 7 pages of card fronts (4×2 grid)
 *     14 card types: 2-10, J, Q, K, A, JOKER
 *     4 suit columns (2 red ♥♦ + 2 black ♠♣)
 *
 * Number cards show pip patterns with images.
 * Face cards (J/Q/K) and JOKER show large center image.
 *
 * Migrated from C# EduFrame.Img10.Generators.Hraci_karty
 */

// Card pip layouts: positions as (x, y) in 0-1 normalized coordinates
const CARD_PIPS: Record<string, [number, number][]> = {
  '2': [
    [0.5, 0],
    [0.5, 1],
  ],
  '3': [
    [0.5, 0],
    [0.5, 0.5],
    [0.5, 1],
  ],
  '4': [
    [0, 0],
    [1, 0],
    [1, 1],
    [0, 1],
  ],
  '5': [
    [0, 0],
    [1, 0],
    [1, 1],
    [0, 1],
    [0.5, 0.5],
  ],
  '6': [
    [0, 0],
    [1, 0],
    [1, 1],
    [0, 1],
    [0, 0.5],
    [1, 0.5],
  ],
  '7': [
    [0, 0],
    [1, 0],
    [1, 1],
    [0, 1],
    [0, 0.5],
    [1, 0.5],
    [0.5, 0.25],
  ],
  '8': [
    [0, 0],
    [1, 0],
    [1, 1],
    [0, 1],
    [0, 0.5],
    [1, 0.5],
    [0.5, 0.25],
    [0.5, 0.75],
  ],
  '9': [
    [0, 0],
    [0, 1 / 3],
    [0, 2 / 3],
    [0, 1],
    [1, 0],
    [1, 1 / 3],
    [1, 2 / 3],
    [1, 1],
    [0.5, 0.5],
  ],
  '10': [
    [0, 0],
    [0, 1 / 3],
    [0, 2 / 3],
    [0, 1],
    [1, 0],
    [1, 1 / 3],
    [1, 2 / 3],
    [1, 1],
    [0.5, 1 / 6],
    [0.5, 5 / 6],
  ],
  J: [],
  Q: [],
  K: [],
  A: [[0.5, 0.5]],
  JOKER: [],
};

const RANK_SYMBOLS: Record<string, ESymbol> = {
  '2': ESymbol.Card2,
  '3': ESymbol.Card3,
  '4': ESymbol.Card4,
  '5': ESymbol.Card5,
  '6': ESymbol.Card6,
  '7': ESymbol.Card7,
  '8': ESymbol.Card8,
  '9': ESymbol.Card9,
  '10': ESymbol.Card10,
  A: ESymbol.CardA,
  J: ESymbol.CardJ,
  Q: ESymbol.CardQ,
  K: ESymbol.CardK,
};

const SUIT_SYMBOLS = [ESymbol.CardHearts, ESymbol.CardDiamond, ESymbol.CardSpade, ESymbol.CardClub];

export class HraciKarty implements ILayoutImageGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 10;
  }
  countMax(): number {
    return 10;
  }

  public async generate(pdf: PdfGen, opts: OptsImage): Promise<void> {
    const margin = PdfUnit.FromMillimeters(5);
    const seed = opts.data.random.next(0, 2147483647);

    const cardW = PdfUnit.FromMillimeters(57);
    const cardH = PdfUnit.FromMillimeters(87);
    const imgHeadW = PdfUnit.FromMillimeters(50);
    const imgHeadH = PdfUnit.FromMillimeters(50);
    const imgSmallW = PdfUnit.FromMillimeters(20);
    const imgSmallH = PdfUnit.FromMillimeters(20);
    const imgAceW = PdfUnit.FromMillimeters(30);
    const imgAceH = PdfUnit.FromMillimeters(30);
    const smallAreaW = PdfUnit.FromMillimeters(25);
    const smallAreaH = PdfUnit.FromMillimeters(50);

    const originX = (PdfSize.A4H.width - 4 * cardW) / 2;
    const originY = (PdfSize.A4H.height - 2 * cardH) / 2;

    const countX = 4;
    const countY = 2;

    const cardKeys = Object.keys(CARD_PIPS);
    const backColors = [PdfColor.fromRgb(0, 0, 255), PdfColor.fromRgb(255, 0, 0)];

    let pageIndex = 0;
    const pages: PageGen[] = [];

    const drawBackPage = async (page: PageGen, backColor: PdfColor) => {
      for (let y = 0; y < countY; y++) {
        for (let x = 0; x < countX; x++) {
          const r = new PdfRect(originX + x * cardW, originY + y * cardH, cardW, cardH);
          await drawDitherLogo(page, opts.data.lang.Cards_CARDS ?? 'KARTY', backColor, r, seed);
        }
      }
    };

    const drawFrontPage = (page: PageGen, cardStartIndex: number) => {
      page.cutCross(new PdfPoint(originX, originY));
      page.cutCross(new PdfPoint(originX + countX * cardW, originY + countY * cardH));

      for (let y = 0; y < countY; y++) {
        const keyIdx = (cardStartIndex + y) % cardKeys.length;
        const cardKey = cardKeys[keyIdx];
        const pips = CARD_PIPS[cardKey];
        const isJoker = cardKey === 'JOKER';

        const itemIdx = keyIdx % opts.list.length;
        const {image} = opts.list[itemIdx];

        for (let x = 0; x < countX; x++) {
          const r = new PdfRect(originX + x * cardW, originY + y * cardH, cardW, cardH);
          const isRed = x < 2;
          const color = isRed ? PdfColor.fromRgb(255, 0, 0) : PdfColor.fromRgb(0, 0, 0);
          const brush = new PdfBrush(color);

          const mx = PdfUnit.FromMillimeters(5);
          const my = PdfUnit.FromMillimeters(8);
          const h = PdfUnit.FromMillimeters(10);

          const r1 = new PdfRect(r.x + mx, r.y + my, r.width - 2 * mx, h);
          const r2 = new PdfRect(r.x + mx, r.y + r.height - my - h, r.width - 2 * mx, h);

          if (pips.length === 0) {
            const imgOrigin = new PdfPoint(
              r.x + (r.width - imgHeadW) / 2,
              r.y + (r.height - imgHeadH) / 2
            );
            const imgRect = new PdfRect(imgOrigin.x, imgOrigin.y, imgHeadW, imgHeadH);

            if (isJoker) {
              if (x === 0) {
                const peterDotSize = PdfUnit.FromMillimeters(6);
                const bigDotSize = PdfUnit.FromMillimeters(35);

                const drawDot = (px: number, py: number, sz: number) => {
                  page.drawEllipse(
                    null,
                    PdfBrush.Black,
                    new PdfRect(px - sz / 2, py - sz / 2, sz, sz)
                  );
                };

                drawDot(r1.x, r1.y, peterDotSize);
                drawDot(r1.right, r1.y, peterDotSize);
                drawDot(r2.x, r2.y + r2.height, peterDotSize);
                drawDot(r2.right, r2.y + r2.height, peterDotSize);

                const cx = r.x + r.width / 2;
                const cy = r.y + r.height / 2;
                const peterRect = new PdfRect(
                  cx - bigDotSize / 2,
                  cy - bigDotSize / 2,
                  bigDotSize,
                  bigDotSize
                );
                page.drawEllipse(null, PdfBrush.Black, peterRect);
                page.drawString(
                  opts.data.lang.Cards_PETR ?? 'PETR',
                  GUtil.baseFont,
                  new PdfBrush(PdfColor.fromRgb(255, 255, 255)),
                  null,
                  peterRect,
                  ETextAlign.Center
                );
              } else {
                const jokerFont = new PdfFont(PdfFont.FONT_NAME, 12);
                page.drawImageCenter(image, imgRect);

                for (let i = 0; i < cardKey.length; i++) {
                  const p1a = new PdfRect(r1.x - 6, r1.y + i * 12, 12, 12);
                  const p1b = new PdfRect(r1.right - 6, r1.y + i * 12, 12, 12);

                  page.drawString(
                    `${cardKey[i]}`,
                    jokerFont,
                    PdfBrush.Black,
                    null,
                    p1a,
                    ETextAlign.Center
                  );
                  page.drawString(
                    `${cardKey[i]}`,
                    jokerFont,
                    PdfBrush.Black,
                    null,
                    p1b,
                    ETextAlign.Center
                  );

                  page.save();
                  page.rotateAtTransform(
                    180,
                    new PdfPoint(r2.x + r2.width / 2, r2.y + r2.height / 2)
                  );

                  const p2a = new PdfRect(r2.x - 6, r2.y + i * 12, 12, 12);
                  const p2b = new PdfRect(r2.right - 6, r2.y + i * 12, 12, 12);

                  page.drawString(
                    `${cardKey[i]}`,
                    jokerFont,
                    PdfBrush.Black,
                    null,
                    p2a,
                    ETextAlign.Center
                  );
                  page.drawString(
                    `${cardKey[i]}`,
                    jokerFont,
                    PdfBrush.Black,
                    null,
                    p2b,
                    ETextAlign.Center
                  );
                  page.restore();
                }
              }
            } else {
              page.drawImageCenter(image, imgRect);
            }
          } else {
            const sz = cardKey === 'A' ? [imgAceW, imgAceH] : [imgSmallW, imgSmallH];

            const smallOriginX = r.x + (r.width - smallAreaW) / 2;
            const smallOriginY = r.y + (r.height - smallAreaH) / 2;

            const sortedPips = [...pips].sort((a, b) => a[1] - b[1] || a[0] - b[0]);

            for (const [px, py] of sortedPips) {
              const ppx = smallOriginX + px * smallAreaW;
              const ppy = smallOriginY + py * smallAreaH;
              const pipRect = new PdfRect(ppx - sz[0] / 2, ppy - sz[1] / 2, sz[0], sz[1]);
              page.drawImageCenter(image, pipRect);
            }
          }

          if (!isJoker) {
            const rankSymbol = RANK_SYMBOLS[cardKey];
            const suitSymbol = SUIT_SYMBOLS[x];

            const rankRect1TL = new PdfRect(r1.x - 6, r1.y - 6, 12, 12);
            const suitRect1BL = new PdfRect(r1.x - 6, r1.y + r1.height - 6, 12, 12);
            const rankRect1TR = new PdfRect(r1.right - 6, r1.y - 6, 12, 12);
            const suitRect1BR = new PdfRect(r1.right - 6, r1.y + r1.height - 6, 12, 12);

            page.drawSymbol(rankSymbol, brush, rankRect1TL, 24, ETextAlign.Center);
            page.drawSymbol(suitSymbol, brush, suitRect1BL, 24, ETextAlign.Center);
            page.drawSymbol(rankSymbol, brush, rankRect1TR, 24, ETextAlign.Center);
            page.drawSymbol(suitSymbol, brush, suitRect1BR, 24, ETextAlign.Center);

            page.save();
            page.rotateAtTransform(180, new PdfPoint(r2.x + r2.width / 2, r2.y + r2.height / 2));

            const rankRect2TL = new PdfRect(r2.x - 6, r2.y - 6, 12, 12);
            const suitRect2BL = new PdfRect(r2.x - 6, r2.y + r2.height - 6, 12, 12);
            const rankRect2TR = new PdfRect(r2.right - 6, r2.y - 6, 12, 12);
            const suitRect2BR = new PdfRect(r2.right - 6, r2.y + r2.height - 6, 12, 12);

            page.drawSymbol(rankSymbol, brush, rankRect2TL, 24, ETextAlign.Center);
            page.drawSymbol(suitSymbol, brush, suitRect2BL, 24, ETextAlign.Center);
            page.drawSymbol(rankSymbol, brush, rankRect2TR, 24, ETextAlign.Center);
            page.drawSymbol(suitSymbol, brush, suitRect2BR, 24, ETextAlign.Center);

            page.restore();
          }

          page.drawRectangle(GUtil.cutPen, null, r);
        }
      }
    };

    // Phase 1: Blue fronts (7 pages)
    for (let currentP = 0; currentP < cardKeys.length; currentP += 2) {
      pages.push(pdf.addPage(true));
      drawFrontPage(pages[pageIndex], currentP);
      pageIndex++;
    }

    // Phase 2: Blue back (1 page)
    pages.push(pdf.addPage(true));
    await drawBackPage(pages[pageIndex], backColors[0]);
    pageIndex++;

    // Phase 3: Red fronts (7 pages)
    for (let currentP = 0; currentP < cardKeys.length; currentP += 2) {
      pages.push(pdf.addPage(true));
      drawFrontPage(pages[pageIndex], currentP);
      pageIndex++;
    }

    // Phase 4: Red back (1 page)
    pages.push(pdf.addPage(true));
    await drawBackPage(pages[pageIndex], backColors[1]);
    pageIndex++;
  }
}
